import { Button, List, Space, Tag, Typography } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Location } from '@/models';
import { metersToKm } from '@/utils';

const { Text } = Typography;

type LocationListProps = {
  header?: ReactNode;
  footer?: ReactNode;
  locations?: Location[];
  isLoading: boolean;
  onEdit?: (location: Location) => void;
  onDelete?: (id: string) => void;
  onRemove?: (id: string) => void;
};

export const LocationList = ({
  header,
  footer,
  locations,
  isLoading,
  onEdit,
  onDelete,
  onRemove,
}: LocationListProps) => {
  const { t } = useTranslation();

  const formatRadius = (radiusInMeters: number) => {
    return `${metersToKm(radiusInMeters)} km`;
  };

  return (
    <List
      size='small'
      header={header}
      footer={footer}
      itemLayout='horizontal'
      dataSource={locations}
      loading={isLoading}
      locale={{ emptyText: t('common:noLocations') }}
      renderItem={(item) => (
        <List.Item
          actions={[
            onEdit && (
              <Button
                aria-label={t('common:edit')}
                type='link'
                key={`edit-${item.id}`}
                onClick={() => onEdit(item)}
              >
                {t('common:edit')}
              </Button>
            ),
            onDelete && (
              <Button
                aria-label={t('common:delete')}
                type='link'
                danger
                key={`delete-${item.id}`}
                onClick={() => onDelete(item.id)}
              >
                {t('common:delete')}
              </Button>
            ),
            onRemove && (
              <Button
                aria-label={t('common:remove')}
                type='link'
                danger
                key={`remove-${item.id}`}
                onClick={() => onRemove(item.id)}
              >
                {t('common:remove')}
              </Button>
            ),
          ].filter(Boolean)}
        >
          <List.Item.Meta
            title={
              <Space>
                {item.name}
                <Tag color='blue'>{formatRadius(item.radius)}</Tag>
                {item.resourceCount !== undefined && (
                  <Tag>{t('common:resourceCount', { count: item.resourceCount })}</Tag>
                )}
              </Space>
            }
            description={
              <Text type='secondary'>
                {item.street}, {item.zip} {item.city}, {item.state}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  );
};
